import React from 'react';
import styled from 'styled-components';
import { Button, useScreenQuery } from 'playup-web-core';
import { AiFillCheckCircle, AiFillExclamationCircle } from 'react-icons/ai';
import { useHistory } from 'react-router-dom';

import ReusableModalPlain from '../ReusableModal/ReusableModalPlain';
import GreenIdModal from '../../Account/components/Profile/GreenIdModal';
import { useDisclosure } from '../../hooks/useDisclosure';
import PortalSessionContext from '../../contexts/PortalSessionContext/PortalSessionContext';
import GlobalContext from '../../contexts/GlobalContext';
import { useTutorialContextProvider } from '../../contexts/TutorialContext';

const AccountVerificationModal = () => {
	const { isSmall } = useScreenQuery();
	const [errorMessage, setErrorMessage] = React.useState<string>('');
	const { isOpen, onClose: dismissGreenId, onOpen } = useDisclosure();
	const { refreshPortalUser } = React.useContext(PortalSessionContext);
	const { updateNotifications } = React.useContext(GlobalContext);
	const [status, setStatus] = React.useState<'VERIFIED' | 'FAIL' | ''>('');
	const history = useHistory();
	const { openTutorialModal } = useTutorialContextProvider();

	const handleFailClose = React.useCallback(() => {
		history.replace('/');
		openTutorialModal();

		updateNotifications((currentNotifications) => [
			...currentNotifications,
			{
				id: new Date().getTime(),
				title: 'Account Locked',
				message: 'Your account is locked, please verify your account before proceeding any further.',
				type: 'warning',
				link: {
					name: 'Verify',
					url: '/account/profile',
				},
			},
		]);
	}, [updateNotifications, history, openTutorialModal]);

	const onVerificationSuccess = React.useCallback(() => {
		setStatus('VERIFIED');
	}, []);

	const onVerificationFail = React.useCallback((reason) => {
		setStatus('FAIL');
		setErrorMessage(reason);
	}, []);

	return (
		<StyledModal
			isSmall={isSmall}
			wrapperStyle={{
				alignItems: isSmall ? 'flex-end' : undefined,
			}}
		>
			<HeaderText>Account Verification</HeaderText>
			<DialogContainer>
				{status !== '' ? (
					<AlertContainer>
						{status === 'FAIL' ? <StyledAiFillExclamationCircle size={75} /> : <StyledAiFillCheckCircle size={75} />}
						<ResultText isSmallScreen={isSmall}>
							{status === 'FAIL'
								? 'Your account has not yet been verified.'
								: ' Your account has been successfully verified!'}
						</ResultText>
						{status === 'FAIL' && (
							<>
								<ContentText
									style={{
										color: 'gray',
										textAlign: 'center',
									}}
								>
									You will not be able to deposit until successfully verification. You may also update your details
									yourself via your account profile and try again.
								</ContentText>
								<a className="_text-blue-700 _underline" href="mailto: support@playup.com.au">
									Contact Customer Service
								</a>
							</>
						)}
						{errorMessage && <ContentText>Reason: {errorMessage}</ContentText>}
						<ResultButton
							kind="white"
							onClick={() => {
								if (status === 'FAIL') {
									handleFailClose();
								} else {
									history.replace('?modal=onboarding-deposit');
								}
							}}
						>
							Continue
						</ResultButton>
					</AlertContainer>
				) : (
					<>
						Welcome {window.portal?.user.username()}!
						<ContentText>
							Your ID has been checked, however, we still require additional information to verify your account.
						</ContentText>
						<Button kind="white" onClick={onOpen}>
							<ButtonText>Verify your ID</ButtonText>
						</Button>
						<NotNowButton isSmall={isSmall} onClick={handleFailClose}>
							Not Now
						</NotNowButton>
					</>
				)}
			</DialogContainer>

			{isOpen && (
				<GreenIdModal
					onVerificationFail={onVerificationFail}
					onVerificationSuccess={onVerificationSuccess}
					onClose={dismissGreenId}
				/>
			)}
		</StyledModal>
	);
};

export default AccountVerificationModal;

const StyledAiFillCheckCircle = styled(AiFillCheckCircle)(({ theme }) => ({
	color: '#01CA6E',
	margin: '20px 0',
}));
const StyledAiFillExclamationCircle = styled(AiFillExclamationCircle)(({ theme }) => ({
	color: theme.colors.danger,
	margin: '20px 0',
}));
const ResultText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	fontSize: '22px',
	fontWeight: 600,
	marginBottom: '10px',
	textAlign: 'center',
	lineHeight: '28px',
	width: isSmallScreen ? '70%' : '100%',
}));

const ResultButton = styled(Button)`
	margin-top: 60px;
	width: 80%;
`;

const AlertContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const WarningText = styled.span`
	flex-direction: row;
	display: flex;
	color: #707070;
	text-align: center;
	font-size: 12px;
	align-items: center;
	justify-content: center;
	font-weight: 400;
`;

const NotNowButton = styled(Button)<{ isSmall: boolean }>`
	font-size: ${({ isSmall }) => (isSmall ? '12' : '14')}px;
	text-decoration: underline;
	color: black;
`;

const HeaderText = styled.span`
	color: #ffffff;
	font-size: 16px;
	font-weight: bold;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledModal = styled(ReusableModalPlain)<{ isSmall: boolean }>`
	max-width: 1200px;
	width: ${({ isSmall }) => (isSmall ? '100' : '35')}%;
	${({ isSmall }) => (isSmall ? 'height: 100%' : 'max-height:  90vh;')};
	margin: 0 auto;
	text-align: center;
	color: black;
	overflow-y: scroll;
	padding: 5px 0;
	display: flex;
	flex-direction: column;
	background-color: #22272e;
	h1 {
		font-size: 20px;
		font-weight: 600;
		margin-bottom: 5px;
	}
	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-width: none;
	.footer {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		width: 90%;
		max-width: 360;
		margin: auto;
		flex-grow: 1;
	}
`;

const ButtonText = styled.span`
	font-size: 15px;
`;

const ContentText = styled.span`
	font-size: 14px;
	font-weight: 400;
	margin-top: 8px;
	margin-bottom: 47px;
`;

const DialogContainer = styled.div`
	background-color: #ffffff;
	padding-left: 8px;
	padding-right: 8px;
	padding-top: 20px;
	padding-bottom: 23px;
	border-radius: 6px;
	display: flex;
	margin-left: 20px;
	margin-right: 20px;
	font-size: 15px;
	font-weight: bold;
	flex-direction: column;
	text-align: start;
	margin-bottom: 20px;
`;
